import {
    checkIsValid,
    clearFormat,
    findObjById,
    convertToMonetary,
    formatNullWithValue,
    formatNullValues,
    formatDate,
} from 'utilities/util';
import CostColumnsData from './CostColumnsData';
import { CostRevEmptyData } from 'constants/costRevenue';
import ReactDOM from 'react-dom';
import {
    costRevModel,
    ICostRevDistribution,
    IChargeItems,
    IQty,
    QuantityDistributionModel,
} from 'constants/costRevModels';
import { costAndRevErrorMessage, FinOpsStatus } from './models';
import * as c from 'views/customer-billing/issueInvoices/details/constants';
import { ExportJobsDetailsColumnsData } from './ExportJobsDetailsColumnsData';
import { ISortModel, SORT_ORDER } from 'reducers/filters/models';
import moment from 'moment';

const parseDate = (val: string | null | undefined) =>
    val && val !== '-'
        ? moment(
            val,
            ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm:ss'],
            true
        ).isValid()
            ? moment(val).format('YYYY-MM-DDTHH:mm:ss')
            : null
        : null;

export const getIndex = (object: any[], id: string): number => {
    if (Array.isArray(object)) {
        return object.length === 0 ? 0 : object[object.length - 1][id];
    }
    return -1;
};
export const checkType = (element: any): string => {
    if (
        element.itemCategory === CostColumnsData.TYPES.COST ||
        (!element.itemCategory &&
            element.payToParty &&
            element.payToParty.length > 0)
    ) {
        return CostColumnsData.TYPES.COST;
    }
    return CostColumnsData.TYPES.REVENUE;
};
export const formatDropDown = (
    baseObj: any[],
    searchLabel: string | string[],
    searchValue: string,
): any[] => {
    const fArray = (element: any): any => {
        let str = '';
        // GET FIRST CODE
        let val = element[searchLabel[0]];
        for (const child of searchLabel) {
            str +=
                str.length > 0
                    ? ` - ${element[child]
                        ? element[child]
                        : ''
                    }`
                    : `${element[child]}`;
        }
        // GET SECOND CODE
        if (searchLabel.length === 3) {
            val = element[searchLabel[1]];
        }
        return { label: str, value: val };
    };

    return baseObj.map((element: any) => {
        if (typeof element === 'object' && element !== null) {
            if (searchLabel && Array.isArray(searchLabel)) {
                return fArray(element);
            }
            return {
                label: element[searchLabel],
                value: element[searchValue],
            };
        } else {
            return { label: element, value: element };
        }
    });
};
export const SYSTEM_INSERT_USERS = ['ATHENA', 'SAP'];
// A charge added from the UI carries the employee id in insertBy, so anything not
// stamped by a system user is user-inserted and stays editable/deletable.
export const isInsertedByUserCheck = (baseData: any[] | null): boolean =>
    !!baseData &&
    baseData.length > 0 &&
    baseData.every((element: any) => !SYSTEM_INSERT_USERS.includes(element.insertBy));
export const checkIfSystemType = (baseData: any[], isInsertedByUser: boolean = false): boolean => {
    let isSystem: boolean = false;
    baseData.forEach((element: any) => {
        switch (element.itemType) {
            case 'SYSTEM':
            case 'STANDARD':
            case 'AUTO':
            case 'SAP':
            case 'ATHENA':
                isSystem = true;
                break;
        }
    });
    return isInsertedByUser ? isInsertedByUser : isSystem;
};
// IRN is optional; when filled it must be alphanumeric and at most 100 characters.
// Returns the message to show, or '' when the value is acceptable.
export const getInternalReferenceNumberError = (value: any): string => {
    if (!value) {
        return '';
    }
    const internalReferenceNumber = String(value);
    if (internalReferenceNumber.length > 100) {
        return costAndRevErrorMessage.errorMsgInternalReferenceNumberLength;
    }
    if (!/^[a-zA-Z0-9]+$/.test(internalReferenceNumber)) {
        return costAndRevErrorMessage.errorMsgInternalReferenceNumber;
    }
    return '';
};
export const validateAllData = (data: any[], target: string) => {
    const validationList = data.map((elData) => {
        const checkValid =
            target === CostColumnsData.TYPES.COST
                ? checkIsValid(
                    elData,
                    CostColumnsData.requiredCostData(),
                )
                : checkIsValid(
                    elData,
                    CostColumnsData.requiredRevenueData(),
                );
        return checkValid;
    });
    const counter = validationList.filter((ind: any) => {
        return ind.isValid === false;
    });
    return { totalInvalid: counter.length, list: counter, validationList };
};
export const calculateTotal = (
    isLocal: boolean = false,
    currentData: any,
): any => {
    let { rate, exchangeRate, quantity } = currentData;
    rate = clearFormat(rate);
    quantity = clearFormat(quantity);
    let calculate = Number(rate) * Number(quantity);
    if (isLocal) {
        exchangeRate = exchangeRate
            ? clearFormat(exchangeRate)
            : CostRevEmptyData.exchangeRate;
        calculate = convertToMonetary(
            Number(rate) *
            Number(quantity) *
            Number(exchangeRate),
        );
        currentData.totalLocalCurrency = !isNaN(calculate)
            ? calculate
            : '0.00';
    } else {
        calculate = !isNaN(calculate)
            ? convertToMonetary(calculate)
            : '0.00';
        currentData.total = calculate;
    }
    if (calculate) {
        return calculate;
    }
    return '0.00';
};
export const formatDates = (element: any) => {
    const doFormat = (val: string) => (val ? formatDate(val) : '-');
    element.insertDate = doFormat(element.insertDate);
    element.billingDate = doFormat(element.billingDate);
    element.updateDate = doFormat(element.updateDate);
    element.pricingDate = doFormat(element.pricingDate);
    element.contractStartDate = doFormat(element.contractStartDate);
    element.contractEndDate = doFormat(element.contractEndDate);
    return element;
};
export const convertNumbers = (
    element: any | any[],
    noFormatting: boolean = false,
    isSpecialPricingCondition: boolean = false,
) => {
    const runCheck = (el: any) => {
        el.total = isSpecialPricingCondition ? convertToMonetary(el.netValue) : calculateTotal(false, el);
        el.totalLocalCurrency = Number(el.total.replace(/,/g, '')) * Number(el.exchangeRate);
        el.rate = el.rate ? convertToMonetary(el.rate) : '0.00';
        el.exchangeRate = el.exchangeRate
            ? parseFloat(String(el.exchangeRate)).toFixed(5)
            : '';
        el.quantity = parseFloat(String(el.quantity)).toFixed(3);
        if (noFormatting) {
            el.total = clearFormat(el.total);
            el.totalLocalCurrency = clearFormat(
                el.totalLocalCurrency,
            );
            el.rate = clearFormat(el.rate);
            if (el.exchangeRate !== '') {
                el.exchangeRate = clearFormat(el.exchangeRate);
            }
            el.quantity = clearFormat(el.quantity);
        }
        return el;
    };
    if (typeof element === 'object' && !Array.isArray(element)) {
        return runCheck(element);
    }
    return element.map((child: any) => {
        return runCheck(child);
    });
};
export const formatChildren = (
    elements: any[],
    quantity: number = 0,
    etsId: number = 0,
) => {
    const formatNumber = (
        value: number,
        overwrite: number,
        toFixedNum: number,
    ) => {
        if (overwrite && Number(overwrite)) {
            return Number(overwrite).toFixed(3);
        } else if (value && Number(value) > 0) {
            return Number(value).toFixed(toFixedNum);
        }
        return overwrite
            ? Number(overwrite).toFixed(3)
            : value
                ? value.toFixed(3)
                : 0.0;
    };
    elements.forEach((element: any) => {
        element.quantity = formatNumber(element.quantity, quantity, 3);
        element.container = formatNullValues(
            [element.containerSize, element.containerType],
            '-',
        );
        element.substitution = formatNullValues(
            [element.substitutionSize, element.substitutionType],
            '-',
        );
        element.containerSize = formatNullWithValue(
            element.containerSize,
            '',
        );
        element.substitutionSize = formatNullWithValue(
            element.substitutionSize,
            '',
        );
        element.containerType = formatNullWithValue(
            element.containerType,
            '',
        );
        element.substitutionType = formatNullWithValue(
            element.substitutionType,
            '',
        );
        element.essoRateDetailsSplitId = element.essoRateDetailsSplitId
            ? formatNullWithValue(
                element.essoRateDetailsSplitId,
                '',
            )
            : element.rateDetailSplitId;
        element.Select = true;
        if (etsId && etsId !== 0) {
            element.etsId = etsId;
        }
        return element;
    });
    return elements;
};
export const persistMaterialInfo = (
    element: any,
    materialMasterList: any[],
) => {
    element.materialCode = element.chargeItem;
    const objList: { name: string; } = findObjById(
        element.chargeItem,
        materialMasterList,
        'materialNo',
    );
    element.materialDescription =
        objList && objList.name ? objList.name : '';
    return element;
};
export const persistParty = (element: any, list: any[], type: string) => {
    let objList: any = findObjById(element.billToParty, list, 'vendorCode');
    element.billToPartyName =
        objList && objList.vendorName ? objList.vendorName : '';
    if (type === CostColumnsData.TYPES.COST) {
        objList = findObjById(element.payToParty, list, 'vendorCode');
        element.payToPartyName =
            objList && objList.vendorName ? objList.vendorName : '';
    }
    return element;
};
export const removeByEssoId = (
    essoId: any | any[],
    baseListObj: any[],
    propertyName?: string,
) => {
    const baseObj = [...baseListObj];
    return baseObj.filter((element: any) => {
        if (Array.isArray(essoId)) {
            if (propertyName) {
                return !essoId.includes(element[propertyName]);
            } else {
                return !essoId.includes(
                    element.essoRateDetailId,
                );
            }
        }
        if (propertyName) {
            return essoId !== element[propertyName];
        }
        return essoId !== element.essoRateDetailId;
    });
};

export const haveLoginData = (nicknameData: any): boolean => {
    if (nicknameData) {
        return true;
    }
    return false;
};
export const updateFooterTotals = (
    target: any,
    props: any[],
    domTarget: string,
) => {
    const totalFooter =
        ReactDOM.findDOMNode &&
        (ReactDOM.findDOMNode(target) as HTMLElement);
    if (totalFooter && totalFooter.querySelector(domTarget)) {
        let totalLocalCurrencyIndex = 0;
        const header =
            totalFooter.querySelectorAll('.data-table.header');
        if (header) {
            header.forEach((domEl: any, index: number) => {
                if (
                    domEl.innerText &&
                    domEl.innerText === 'Local Amount'
                ) {
                    totalLocalCurrencyIndex = index;
                }
            });
        }
        const list = totalFooter.querySelectorAll(
            '.rt-tfoot .totalLocalCurrency div',
        );
        if (list && totalLocalCurrencyIndex > 0) {
            totalLocalCurrencyIndex = 0;
            if (
                list[totalLocalCurrencyIndex] &&
                list[totalLocalCurrencyIndex].querySelectorAll(
                    'span',
                )
            ) {
                list[totalLocalCurrencyIndex]!.querySelectorAll(
                    'span',
                )[1]!.innerHTML = calculateTotalOfField(
                    props,
                    'totalLocalCurrency',
                );
            }
        }
    }
};
export const calculateTotalOfField = (
    baseListObj: any[],
    fieldName: string,
    float: number = 2,
    ignoreCanceled: boolean = false,
) => {
    const baseObj = [...baseListObj];
    let total = 0;
    baseObj.forEach((element: any) => {
        const newVal = clearFormat(element[fieldName]);
        const checkStatus =
            !ignoreCanceled ||
            (ignoreCanceled &&
                (!element.opsStatus ||
                    (element.opsStatus &&
                        element.opsStatus.toLowerCase() !==
                        'cancelled')));
        if (newVal && !isNaN(newVal) && checkStatus) {
            total = newVal + total;
        }
    });
    return convertToMonetary(total, float);
};
export interface IDivisionBreakdownRow {
    division: string;
    revenue: number;
    cost: number;
    profit: number;
}
export interface IDivisionBreakdown {
    rows: IDivisionBreakdownRow[];
    total: IDivisionBreakdownRow;
}
// Groups cost/revenue charge items by their `division` (e.g. SCM, MCI) and
// returns per-division revenue/cost/profit plus an overall total row.
export const calculateDivisionBreakdown = (
    cost: any[] = [],
    revenue: any[] = [],
    fieldName: string = 'totalLocalCurrency',
): IDivisionBreakdown => {
    const byDivision: { [division: string]: IDivisionBreakdownRow } = {};
    const getRow = (division: string): IDivisionBreakdownRow => {
        const key = division || 'Unassigned';
        if (!byDivision[key]) {
            byDivision[key] = { division: key, revenue: 0, cost: 0, profit: 0 };
        }
        return byDivision[key];
    };
    const accumulate = (items: any[], target: 'revenue' | 'cost') => {
        items.forEach((element: any) => {
            const value = clearFormat(element[fieldName]);
            if (value && !isNaN(value)) {
                getRow(element.division)[target] += value;
            }
        });
    };
    accumulate(revenue, 'revenue');
    accumulate(cost, 'cost');

    const total: IDivisionBreakdownRow = { division: 'Total', revenue: 0, cost: 0, profit: 0 };
    const rows = Object.keys(byDivision).map((key: string) => {
        const row = byDivision[key];
        row.profit = row.revenue - row.cost;
        total.revenue += row.revenue;
        total.cost += row.cost;
        return row;
    });
    total.profit = total.revenue - total.cost;
    return { rows, total };
};
export const converICB = (icb: any): string => {
    if (icb === 'true' || icb === true) {
        return 'Y';
    } else if (icb === 'Y') {
        return 'true';
    } else if (icb === 'N') {
        return 'false';
    } else if (icb === 'false' || icb === false) {
        return 'N';
    }
    return icb;
};
export const getEmptyCostRev = (
    type: string,
    id: number,
    chargeItems?: IChargeItems[],
): any => {
    const objResponse: ICostRevDistribution = costRevModel;
    objResponse.ItemCategory = type;
    objResponse.ChargeItems = chargeItems
        ? chargeItems
        : [costRevModel.ChargeItems[0]];
    const newItem: any[] = [];
    objResponse.ChargeItems.forEach((cItem: any) => {
        const nItem: any = {};
        if (cItem.IsIgnoredOnRateRefresh !== undefined) {
            nItem.IsIgnoredOnRateRefresh = cItem.IsIgnoredOnRateRefresh;
        }
        nItem.ChargeDetailId = cItem.essoRateDetailId || 0;
        nItem.Distribution =
            cItem.sourceType === 'ETS' ? 1 : cItem.distribution;
        nItem.SourceType = cItem.sourceType;
        nItem.SourceID = id;
        nItem.ItemNumber = cItem.itemNumber;
        nItem.ItemType = cItem.itemType;
        nItem.ItemCategory = cItem.itemCategory;
        nItem.IsDeleted = cItem.IsDeleted || cItem.isDeleted;
        nItem.ModsStringRevenue = cItem.modsStringRevenue || '';
        nItem.MaterialCode = cItem.materialCode;
        nItem.MaterialDescription =
            cItem.MaterialDescription || cItem.materialDescription;
        nItem.Quantity = cItem.quantity;
        nItem.Uom = cItem.uom;
        nItem.RowVersion = cItem.rowVersion;
        nItem.Rate = cItem.rate;
        nItem.CondCurrency = cItem.condCurrency;
        nItem.PricingDate = cItem.pricingDate;
        nItem.ExchangeRate = cItem.exchangeRate;
        nItem.LocalCurrency = cItem.localCurrency;
        nItem.ProfitCenterRevenue = cItem.profitCenterRevenue;
        nItem.PayToParty = cItem.payToParty;
        nItem.PayToPartyName = cItem.payToPartyName;
        nItem.BillToParty = cItem.billToParty;
        nItem.BillToPartyName = cItem.billToPartyName;
        nItem.IcbIndicator = cItem.icbIndicator
            ? cItem.icbIndicator
            : '-';
        nItem.PayTermsRevenue = cItem.payTermsRevenue || '';
        nItem.PurOrgCost = cItem.purOrgCost;
        nItem.PurOrg_Rev = cItem.purOrg_Rev;
        nItem.CarrierServiceContractCost =
            cItem.carrierServiceContractCost;
        nItem.ContractNumberCost = cItem.contractNumberCost;
        nItem.ContractItemNumberCost = cItem.contractItemNumberCost;
        nItem.PartyDetails = cItem.partyDetails;
        nItem.InfoRecordCost = cItem.infoRecordCost;
        nItem.ErrorReason = cItem.errorReason;
        nItem.PassThrough = cItem.passThrough;
        nItem.ContractNumberRevenue = cItem.contractNumberRevenue;
        nItem.ContractTypeRevenue = cItem.contractTypeRevenue;
        nItem.InsertBy = cItem.insertBy;
        nItem.UpdatedBy = cItem.updatedBy;
        nItem.ReasonCode = cItem.reasonCode;
        nItem.exportSalesOrderId = cItem.exportSalesOrderId;
        nItem.ServiceRenderedAt = getServiceRenderedAt(
            cItem.serviceRenderedAt,
        );
        nItem.SpecialPricingCondition = cItem.specialPricingCondition === 'Y' || cItem.specialPricingCondition === true;
        nItem.NetValue = cItem.netValue ? cItem.netValue : 0.00;
        nItem.VatPartner = cItem.vatPartner;
        nItem.VatPartnerReasonCodeNo = cItem.vatPartnerReasonCodeNo;
        nItem.QuantityDistribution =
            cItem.sourceType !== 'ETS'
                ? getDistributionArr(
                    cItem.children,
                    false,
                    nItem.id,
                    QuantityDistributionModel,
                )
                : [];
        nItem.AdhocContractualAmendmentIdentifier = cItem.AdhocContractualAmendmentIdentifier ?? cItem.adhocContractualAmendmentIdentifier ?? null;
        nItem.dynamicFields = cItem.dynamicFields ?? cItem.DynamicFields ?? null;
        nItem.Applicability = cItem.Applicability ?? cItem.applicability ?? null;
        nItem.ChargeType = cItem.ChargeType ?? cItem.chargeType ?? null;
        nItem.AthenaRateIdentifier = cItem.AthenaRateIdentifier ?? cItem.athenaRateIdentifier ?? null;
        nItem.VendorReference = cItem.vendorReference ?? cItem.VendorReference ?? null;
        nItem.InternalReferenceNumber = cItem.internalReferenceNumber ?? cItem.InternalReferenceNumber ?? null;
        nItem.VatPartnerIdentifier = cItem.VatPartnerIdentifier ?? cItem.vatPartnerIdentifier ?? null;
        nItem.chargeValidityBased =
            (cItem.chargeValidityBased !== '-' && cItem.chargeValidityBased != null) ? cItem.chargeValidityBased :
                (cItem.ChargeValidityBased !== '-' && cItem.ChargeValidityBased != null) ? cItem.ChargeValidityBased :
                    null;

        nItem.contractStartDate = parseDate(cItem.contractStartDate)
            ? parseDate(cItem.contractStartDate)
            : parseDate(cItem.ContractStartDate) || null;
        nItem.contractEndDate = parseDate(cItem.contractEndDate)
            ? parseDate(cItem.contractEndDate)
            : parseDate(cItem.ContractEndDate) || null;

        nItem.AthenaSecondaryRateIdentifier =
            (cItem.secondaryRateIdentifier !== '-' && cItem.secondaryRateIdentifier != null) ? cItem.secondaryRateIdentifier :
                (cItem.SecondaryRateIdentifier !== '-' && cItem.SecondaryRateIdentifier != null) ? cItem.SecondaryRateIdentifier :
                    null;
        // Charge-split: when the charge is split across a shipment level (SBK today), persist the
        // split document number (the SBK / booking number this charge row maps to). Only sent for
        // split charges, so non-split payloads are unchanged.
        const splitDocumentNumber = cItem.splitReferenceNumber ?? cItem.SplitDocumentNumber;
        if (splitDocumentNumber) {
            nItem.SplitDocumentNumber = splitDocumentNumber;
        }
        // SBK x lane split: the lane id the charge row was split on, persisted next to the SBK as
        // the property identifier.
        const propertyIdentifier = cItem.splitLaneId ?? cItem.propertyIdentifier ?? cItem.PropertyIdentifier;
        if (propertyIdentifier) {
            nItem.PropertyIdentifier = propertyIdentifier;
        }
        cItem = nItem;
        newItem.push(nItem);
        return newItem;
    });
    objResponse.ChargeItems = newItem;
    return objResponse;
};

export const getVatPartnerData = (data: any, targetType: string): any => {
    const vatPartners: any[] = [];
    let elementId = 0;
    data.forEach((element: any, index: number) => {
        if (element.vatPartnerReasonCodeNo) {
            elementId = elementId === 0 ? index : elementId;
            vatPartners.push({
                ...(targetType === 'origin' ? { essoRateDetailId: element.essoRateDetailId } : { issoRateDetailId: element.rateDetailId }),
                vatPartnerCode: element.vatPartner,
                vatPartnerReasonCodeNo: element.vatPartnerReasonCodeNo,
            });
        }
    });
    return {
        updatedBy: data[elementId].updatedBy,
        vatPartners
    };
};

export const getServiceRenderedAt = (value: any) => {
    if (value === '-' || value === '') {
        return null;
    }
    if (typeof value === 'string') {
        return value;
    }
    const serviceRenderedAtMap = {
        1: 'ORIGIN',
        2: 'DESTINATION',
        3: 'FREIGHT',
    };
    return value ? serviceRenderedAtMap[value] : null;
};
export const addQuantityDistribution = (
    howManyChildren: number,
    parentId: number = 1,
    data?: any,
): IQty[] => {
    const el: any = QuantityDistributionModel;
    const resp = data ? getDistributionArr(data, true, parentId, el) : [];
    if (!data) {
        for (let i = 0; i < howManyChildren; i++) {
            resp.push(...el);
        }
    }
    return resp;
};
export const getDistributionArr = (
    data: any,
    isNew: boolean,
    parentId: number,
    model: any = costRevModel.ChargeItems[0].QuantityDistribution[0],
): any[] => {
    let newData = [];
    if (data) {
        newData = data.map((item: any, index: number) => {
            const ret = { ...model };
            const newId = index + 1;
            ret.BookingID = item.BookingID || item.bookingId;
            ret.BookingNumber =
                item.BookingNumber || item.bookingNumber || 0;
            ret.BookingSplitId =
                item.BookingSplitId || item.bookingSplitId;
            ret.DocumentId =
                Number(item.DocumentId) ||
                Number(item.documentId) ||
                0;
            ret.DocumentNumber =
                item.DocumentNumber || item.documentNumber || 0;
            ret.CblID =
                formatNullWithValue(item.CblID, 0) ||
                formatNullWithValue(item.cblId, 0) ||
                0;
            ret.cBLNumber =
                formatNullWithValue(item.cBLNumber, '') ||
                formatNullWithValue(item.cblNumber, '') ||
                '';
            ret.EquipmentID =
                formatNullWithValue(item.EquipmentID, 0) ||
                formatNullWithValue(item.equipmentId, 0) ||
                0;
            ret.EquipmentNumber =
                formatNullWithValue(item.EquipmentNumber, '') ||
                formatNullWithValue(item.equipmentNumber, '') ||
                '';
            ret.essoRateDetailsSplitId =
                formatNullWithValue(
                    item.essoRateDetailsSplitId,
                    '',
                ) ||
                formatNullWithValue(
                    item.EssoRateDetailsSplitId,
                    '',
                ) ||
                formatNullWithValue(
                    item.RateDetailsSplitId,
                    '',
                ) ||
                formatNullWithValue(item.rateDetailSplitId, '');
            ret.QuantityPercentage = isNew
                ? 0
                : item.QuantityPercentage || item.quantity || 0;
            ret.quantity = isNew
                ? 0
                : item.QuantityPercentage || item.quantity || 0;
            ret.Select = isNew ? true : item.Select;
            ret.containerSize =
                formatNullWithValue(item.ContainerSize, '') ||
                formatNullWithValue(item.containerSize, '');
            ret.containerType =
                formatNullWithValue(item.ContainerType, '') ||
                formatNullWithValue(item.containerType, '');
            ret.substitutionType =
                formatNullWithValue(
                    item.SubstitutionType,
                    '',
                ) ||
                formatNullWithValue(item.substitutionType, '');
            ret.substitutionSize =
                formatNullWithValue(
                    item.SubstitutionSize,
                    '',
                ) ||
                formatNullWithValue(item.substitutionSize, '');
            ret.etsId =
                formatNullWithValue(item.etsId, '') ||
                formatNullWithValue(
                    item.equipmentTransportShipmentId,
                    '',
                );
            ret.container = formatNullWithValue(
                ret.containerSize + '' + ret.containerType,
                '',
            );
            ret.substitution = formatNullWithValue(
                ret.substitutionSize +
                '' +
                ret.substitutionType,
                '',
            );
            const internalId = parentId
                ? parentId
                : ret.BookingID ||
                ret.DocumentId ||
                ret.CblID ||
                ret.EquipmentID;
            ret.id = `${internalId}.${newId}`;
            return ret;
        });
    }
    return newData;
};
export const convertDataToDialog = (costRevData: any): any[] => {
    const dialogData = [...costRevData];
    return dialogData.map((el: any) => {
        el.chargeItem = el.materialCode;
        el.distribution = el.distribution || 2;
        el.delete = 'Remove';
        el.children = getDistributionArr(el.children, false, el.id);
        return el;
    });
};
export const convertChildren = (costRevDialogData: any): any[] => {
    const dialogData = costRevDialogData;
    return dialogData.map((el: any) => {
        if (el && el.children) {
            el.children.forEach((child: any) => {
                child.bookingId =
                    child.bookingId || child.BookingID;
                child.bookingNumber =
                    child.bookingNumber ||
                    child.BookingNumber;
                child.bookingSplitId =
                    child.bookingSplitId ||
                    child.BookingSplitId;
                child.documentId =
                    child.documentId || child.DocumentId;
                child.documentNumber =
                    child.documentNumber ||
                    child.DocumentNumber;
                child.cblId = child.cblId || child.CblID;
                child.cblNumber =
                    child.cblNumber || child.cBLNumber;
                child.equipmentID =
                    child.equipmentID || child.EquipmentID;
                child.equipmentNumber =
                    child.equipmentNumber ||
                    child.EquipmentNumber;
                child.distribution = child.Distribution;
                child.containerType = child.ContainerType;
                child.containerSize = child.ContainerSize;
                child.substitutionType = child.SubstitutionType;
                child.substitutionSize = child.SubstitutionSize;
                child.quantity = child.QuantityPercentage;
            });
        }
        return el;
    });
};

export const generateDistributionData = (data: any): IQty[] => {
    const resp: IQty[] = [];
    if (data) {
        if (
            data.costAndRevenueSplits &&
            Array.isArray(data.costAndRevenueSplits)
        ) {
            data.costAndRevenueSplits.map(
                (el: any, index: number) => {
                    resp.push({
                        index,
                        BookingID: el.bookingId,
                        BookingNumber: el.bookingNumber,
                        BookingSplitId:
                            formatNullWithValue(
                                el.bookingSplitId,
                                0,
                            ),
                        EquipmentID:
                            formatNullWithValue(
                                el.equipmentId,
                                0,
                            ),
                        EquipmentNumber:
                            formatNullWithValue(
                                el.equipmentNumber,
                                '',
                            ),
                        EssoRateDetailsSplitId:
                            formatNullWithValue(
                                el.essoRateDetailsSplitId,
                                '',
                            ),
                        DocumentId: el.documentId,
                        DocumentNumber:
                            el.documentNumber,
                        CblID: formatNullWithValue(
                            el.cblId,
                            0,
                        ),
                        cBLNumber: formatNullWithValue(
                            el.cblNumber,
                            '',
                        ),
                        QuantityPercentage: 0,
                        SubstitutionType:
                            el.substitutionType,
                        ContainerType: el.containerType,
                        SubstitutionSize:
                            el.substitutionSize,
                        ContainerSize: el.containerSize,
                        Select: true,
                    });
                    return el;
                },
            );
        } else if (data.bookings && data.bookings.length >= 1) {
            data.bookings.map((el: any, index: number) => {
                return resp.push({
                    index,
                    BookingID: el.id,
                    BookingNumber: el.number,
                    EquipmentID: checkDataFromObjOrArr(
                        data,
                        ['equipmentId'],
                        'equipments',
                        0,
                    ),
                    EquipmentNumber: checkDataFromObjOrArr(
                        data,
                        ['equipmentNumber'],
                        'equipments',
                        '',
                    ),
                    DocumentId: checkDataFromObjOrArr(
                        data,
                        ['documentId'],
                        'documents',
                        0,
                    ),
                    DocumentNumber: checkDataFromObjOrArr(
                        data,
                        ['documentNumber'],
                        'documents',
                        '',
                    ),
                    CblID: checkDataFromObjOrArr(
                        data,
                        ['cblId'],
                        'cbls',
                        '',
                    ),
                    cBLNumber: checkDataFromObjOrArr(
                        data,
                        ['cblNumber'],
                        'cbls',
                        '',
                    ),
                    BookingSplitId: checkDataFromObjOrArr(
                        data,
                        ['bookingSplitId'],
                        'bookings',
                        '',
                    ),
                    QuantityPercentage: '0',
                    EssoRateDetailsSplitId:
                        el.essoRateDetailsSplitId,
                    SubstitutionType: el.substitutionType,
                    ContainerType: el.containerType,
                    SubstitutionSize: el.substitutionSize,
                    ContainerSize: el.containerSize,
                    Select: true,
                });
            });
        } else if (data.shipment && data.shipment.bookings) {
            const getFirst = [data.shipment.bookings[0]];
            getFirst.map((el: any, index: number) => {
                resp.push({
                    index,
                    BookingID:
                        el && el.bookingId
                            ? el.bookingId
                            : '',
                    BookingNumber:
                        el && el.number
                            ? el.number
                            : '',
                    DocumentId: checkDataFromObjOrArr(
                        data,
                        ['documentId'],
                        'documents',
                        0,
                    ),
                    DocumentNumber: checkDataFromObjOrArr(
                        data,
                        ['documentNumber'],
                        'documents',
                        0,
                    ),
                    EquipmentID:
                        data.equipmentTransport &&
                        data.equipmentTransport
                            .equipmentTransportId,
                    EquipmentNumber:
                        data.equipmentTransport &&
                        data.equipmentTransport
                            .equipmentNumber,
                    CblID: checkDataFromObjOrArr(
                        data,
                        ['cblId'],
                        'cbls',
                        '',
                    ),
                    cBLNumber: checkDataFromObjOrArr(
                        data,
                        ['cblNumber'],
                        'cbls',
                        '',
                    ),
                    BookingSplitId: 0,
                    QuantityPercentage: '0',
                    EssoRateDetailsSplitId: 0,
                    SubstitutionType:
                        data.equipmentTransport &&
                        data.equipmentTransport
                            .substitutionType,
                    ContainerType:
                        data.equipmentTransport &&
                        data.equipmentTransport.type,
                    SubstitutionSize:
                        data.equipmentTransport &&
                        data.equipmentTransport
                            .substitutionSize,
                    ContainerSize:
                        data.equipmentTransport &&
                        data.equipmentTransport.size,
                    etsId:
                        data &&
                        data.equipmentTransportShipmentId,
                    Select: true,
                });
                return resp;
            });
        } else {
            resp.push({
                index: 1,
                BookingID: data.booking.bookingId,
                BookingNumber: data.booking.bookingNumber,
                EquipmentID: data.equipmentId
                    ? formatNullWithValue(
                        data.equipmentId,
                        0,
                    )
                    : 0,
                EquipmentNumber: data.equipmentNumber
                    ? data.equipmentNumber
                    : '',
                DocumentId: data.documentId
                    ? formatNullWithValue(
                        data.documentId,
                        0,
                    )
                    : 0,
                DocumentNumber: data.documentNumber
                    ? data.documentNumber
                    : '',
                CblID: data.cblId
                    ? formatNullWithValue(data.cblId, 0)
                    : 0,
                cBLNumber: data.cblNumber ? data.cblNumber : '',
                BookingSplitId: data.bookingSplitId
                    ? formatNullWithValue(
                        data.bookingSplitId,
                        0,
                    )
                    : 0,
                QuantityPercentage: '0',
                EssoRateDetailsSplitId:
                    data.EssoRateDetailsSplitId,
                SubstitutionType: data.SubstitutionType,
                ContainerType: data.ContainerType,
                SubstitutionSize: data.SubstitutionSize,
                ContainerSize: data.ContainerSize,
                Select: true,
            });
        }
    }
    return resp;
};
export const checkDataFromObjOrArr = (
    data: any,
    key: string[],
    arrName: any,
    format: any,
): any => {
    let val: any = format;
    val = findInArr(data, key);
    if (
        (!val || val === format) &&
        data[arrName] &&
        data[arrName].length > 0
    ) {
        val = findInArr(data[arrName][0], key);
    }
    return formatNullWithValue(val, format);
};
export const findInArr = (baseData: any, keys: any[]) => {
    let internalVal = null;
    for (const entry of keys) {
        internalVal = baseData[entry] ? baseData[entry] : null;
    }
    return internalVal;
};

export const findAllinArr = (baseData: any, key: string) => {
    const internalVal: any[] = [];
    if (baseData && Array.isArray(baseData)) {
        for (const entry of baseData) {
            if (entry[key]) {
                internalVal.push(entry[key]);
            }
        }
    }
    return internalVal;
};
export const sumInArr = (baseData: any[], key: string): string => {
    let internalVal = 0;
    for (const entry of baseData) {
        internalVal =
            entry && entry[key] && !isNaN(entry[key])
                ? internalVal + entry[key]
                : internalVal;
    }
    return internalVal.toString();
};
export const includes = (
    data: any[],
    field: string,
    value: string,
): boolean => {
    let doesInclude: boolean = false;
    data.forEach((element: any) => {
        if (element[field] === value) {
            doesInclude = true;
        }
    });
    return doesInclude;
};
export const includesStr = (data: any[], key: string, value: string) => {
    let bol: boolean = false;
    if (data) {
        data.some((element: any, index: number) => {
            const elementValue = element[key] || '';
            if (
                elementValue.toLowerCase() ===
                value.toLowerCase()
            ) {
                bol = true;
                return true;
            }
            return;
        });
    }
    return bol;
};
export const typeAndSizeCheck = (data: any, fromDialog: boolean = false) => {
    let doesMatch: boolean = true;
    const arrRep: boolean[] = [];
    let containerSize = '';
    let containerType = '';
    let substitutionSize = '';
    let substitutionType = '';
    if (data && data.length > 0) {
        data.forEach((element: any, index: number) => {
            let target: any[] = element.children;
            let internalMatch: boolean = true;
            if (fromDialog) {
                target = target.filter(
                    (item: any) => item.Select === true,
                );
            }
            target.some((child: any, childIndex: number) => {
                if (childIndex === 0) {
                    containerSize = child.containerSize;
                    containerType = child.containerType;
                    substitutionSize =
                        child.substitutionSize;
                    substitutionType =
                        child.substitutionType;
                }
                if (
                    child.containerSize !== containerSize ||
                    child.containerType !== containerType ||
                    child.substitutionType !==
                    substitutionType ||
                    child.substitutionSize !==
                    substitutionSize
                ) {
                    internalMatch = false;
                }
                return;
            });
            arrRep.push(internalMatch);
        });
        if (arrRep.includes(false)) {
            doesMatch = false;
        }
    }
    return doesMatch;
};
export const checkLinkedState = (data: any[]) => {
    return (
        (data[0].itemCategory === CostColumnsData.TYPES.COST &&
            includesStr(
                data,
                'status',
                'Linked to Supplier Invoice',
            )) ||
        (data[0].itemCategory === CostColumnsData.TYPES.COST &&
            includesStr(
                data,
                'status',
                'Linked to Partial Supplier Invoice',
            )) ||
        (data[0].itemCategory === CostColumnsData.TYPES.REVENUE &&
            includesStr(data, 'status', 'Linked to Final Invoice'))
    );
};
export const FinOpsStatusSalesOrder = (dataItem: string | string[]) => {
    const list = [
        'New',
        'Linked to Sales Order',
        'Linked to Draft Invoice',
    ];
    let bol: boolean = false;
    if (Array.isArray(dataItem)) {
        dataItem.forEach((el: any) => {
            if (el && list.includes(el)) {
                bol = true;
            }
        });
    } else {
        if (dataItem && list.includes(dataItem)) {
            bol = true;
        }
    }
    return bol;
};
// tslint:disable-next-line: one-variable-per-declaration
export const FinOpsStatusPermission = (
    finOpsStatusList: any[],
    systemData: any[] | null,
    target?: string,
): boolean => {
    const bol: boolean[] = [];
    const stripAll = (str: string) =>
        str.trim().toLocaleLowerCase().replace(/\s/g, '');
    finOpsStatusList.forEach((status: string | null) => {
        const finOpsStatus = status ? stripAll(status) : status;
        if (
            finOpsStatus === stripAll(FinOpsStatus.open) ||
            finOpsStatus === null ||
            finOpsStatus === stripAll('') ||
            finOpsStatus === stripAll(FinOpsStatus.rateReceived) ||
            finOpsStatus === stripAll(FinOpsStatus.received)
        ) {
            if (
                (systemData &&
                    checkIfSystemType(systemData, isInsertedByUserCheck(systemData)) || !systemData)
            ) {
                bol.push(true);
            } else {
                bol.push(false);
            }
        } else if (
            finOpsStatus ===
            stripAll(FinOpsStatus.salesOrderCreated) ||
            finOpsStatus ===
            stripAll(FinOpsStatus.salesOrderUpdated)
        ) {
            bol.push(true);
        } else if (
            finOpsStatus === stripAll(FinOpsStatus.rateRequested) ||
            finOpsStatus ===
            stripAll(FinOpsStatus.salesOrderRequested) ||
            finOpsStatus ===
            stripAll(
                FinOpsStatus.salesOrderCreateRequested,
            ) ||
            finOpsStatus ===
            stripAll(
                FinOpsStatus.salesOrderUpdateRequested,
            ) ||
            finOpsStatus ===
            stripAll(FinOpsStatus.invoiceRequested) ||
            finOpsStatus ===
            stripAll(
                FinOpsStatus.salesOrderCancelRequested,
            ) ||
            finOpsStatus ===
            stripAll(
                FinOpsStatus.invoiceCancellationRequested,
            ) ||
            finOpsStatus ===
            stripAll(FinOpsStatus.invoiceInFinopsCreated)
        ) {
            bol.push(false);
        } else if (
            finOpsStatus ===
            stripAll(FinOpsStatus.salesOrderCreated) ||
            finOpsStatus ===
            stripAll(FinOpsStatus.salesOrderUpdated)
        ) {
            bol.push(true);
        } else if (
            finOpsStatus ===
            stripAll(FinOpsStatus.partialInvoiced) ||
            finOpsStatus === stripAll(FinOpsStatus.fullInvoiced) ||
            finOpsStatus ===
            stripAll(FinOpsStatus.partialSettled) ||
            finOpsStatus === stripAll(FinOpsStatus.fullSettled)
        ) {
            if (
                systemData &&
                (systemData[0] || {}).itemCategory ===
                CostColumnsData.TYPES.REVENUE &&
                includesStr(
                    systemData,
                    'status',
                    'Linked to Final Invoice',
                )
            ) {
                bol.push(false);
            } else {
                bol.push(true);
            }
        } else {
            bol.push(false);
        }
    });
    let resp: boolean = true;
    bol.forEach((el: boolean) => {
        if (el === false) {
            resp = false;
        }
    });
    return resp;
};
export const originDestinationCols = (
    isDest: boolean,
    type: string,
    isExportJobdetails?: boolean,
    showAthenaRows?: boolean,
    isReceivingFCR?: boolean,
    isPODetails?: boolean,
    showServiceProvider?: boolean,
    enableBlockedType?: boolean,
    isNftpBillable?: boolean,
    isNftpRoeEnabled?: boolean,
    isMciChargeEnabled?: boolean,
    is4PLChargeLevelEnabled: boolean = false,
    isNonStandardFcrEnabled: boolean = false,
    showReceivingTriggerDocument: boolean = false,
): any[] => {
    if (type === CostColumnsData.TYPES.COST) {
        return isDest
            ? CostColumnsData.getCostColumnDestination(
                showAthenaRows,
                isPODetails,
                enableBlockedType,
                isNftpBillable,
                isNftpRoeEnabled,
                isMciChargeEnabled
            )
            : CostColumnsData.getCostColumn(
                [],
                [],
                false,
                isExportJobdetails,
                showAthenaRows,
                isReceivingFCR,
                isPODetails,
                enableBlockedType,
                isNftpBillable,
                isNftpRoeEnabled,
                isMciChargeEnabled,
                is4PLChargeLevelEnabled,
                showReceivingTriggerDocument
            );
    } else {
        return isDest
            ? CostColumnsData.getRevenueColumnDestination(
                showAthenaRows,
                showServiceProvider,
                enableBlockedType,
                isNftpBillable,
                isNftpRoeEnabled,
                isMciChargeEnabled
            )
            : CostColumnsData.getRevenueColumn(
                [],
                [],
                false,
                false,
                isExportJobdetails,
                showAthenaRows,
                isReceivingFCR,
                showServiceProvider,
                enableBlockedType,
                isNftpBillable,
                isNftpRoeEnabled,
                isMciChargeEnabled,
                is4PLChargeLevelEnabled,
                isNonStandardFcrEnabled
            );
    }
};

export const originDestinationColsNew = (isDest: boolean, enableBlockedType: boolean, isNftpBillable: boolean | undefined, isNftpRoeEnabled: boolean | undefined, type: string, isMciChargeEnabled: boolean = false): any[] => {
    return isDest
        ? CostColumnsData.getCostColumnDestination(false, false, enableBlockedType, isNftpBillable, isNftpRoeEnabled, isMciChargeEnabled)
        : CostColumnsData.getCostorRevenueColumn([], [], false, enableBlockedType, isNftpRoeEnabled, type, isMciChargeEnabled);
};

export const getExportJobsCols = (type: any): any[] => {
    return ExportJobsDetailsColumnsData.getExportJobsDetailsColumn(type);
};

export const getEquipmentCols = (): any[] => {
    return ExportJobsDetailsColumnsData.getEquipmentDetailsColumn();
};

export const CBLChargesCols = (type: string): any[] => {
    if (type === CostColumnsData.TYPES.COST) {
        return CostColumnsData.getCBLCostColumn([], [], false);
    } else {
        return CostColumnsData.getCBLRevenueColumn([], [], false);
    }
};
export const canBeInvoiced = (
    list: any[],
    target: string,
    isDestination: boolean = false,
): any[] => {
    const compareTo: any = {
        billToParty: '',
        companyCode: '',
        billingCurrency: '',
        salesAreaRev: '',
        productType: '',
        cpod: '',
    };
    let returnBol: boolean = list.length > 0 ? true : false;
    const errorIds: any[] = [];
    const hasSameNumber = () => {
        let doesInclude = false;
        const check = isDestination
            ? 'etsNumber'
            : 'primaryDocumentNumber';
        const original: any[] = list.map((e) => e[check]);
        if (target === c.BULK) {
            const unique: any[] = [...new Set(original)].filter(
                (e) => e,
            );
            if (original.length > unique.length) {
                doesInclude = true;
            }
        } else {
            // remove empty
            const unique: any[] = original.filter((e) => e);
            if (original.length > unique.length) {
                doesInclude = true;
            }
        }
        return doesInclude;
    };
    list.forEach((item: any, index: number) => {
        if (index === 0) {
            if (hasSameNumber()) {
                returnBol = false;
            }
            compareTo.companyCode = item.companyCode;
            compareTo.salesAreaRev = item.salesAreaRev;
            if (target !== c.BULK) {
                compareTo.billToParty = item.billToParty;
                compareTo.billingCurrency =
                    item.billingCurrency;
                compareTo.productType = item.productType;
                if (isDestination) {
                    if (item.cpod) {
                        compareTo.cpod = item.cpod;
                    } else {
                        returnBol = false;
                    }
                }
            }
        } else {
            const originProductTypeCheck =
                !isDestination &&
                target !== c.BULK &&
                compareTo.productType &&
                compareTo.productType !== item.productType;
            const notBulkCheck =
                target !== c.BULK &&
                (compareTo.billToParty !== item.billToParty ||
                    !item.billingCurrency ||
                    compareTo.billingCurrency !==
                    item.billingCurrency ||
                    !item.billingCurrency);
            if (
                originProductTypeCheck ||
                notBulkCheck ||
                compareTo.companyCode !== item.companyCode ||
                !item.companyCode ||
                compareTo.salesAreaRev !== item.salesAreaRev ||
                !item.salesAreaRev
            ) {
                returnBol = false;
            }
        }
        if (!returnBol) {
            errorIds.push(item.id);
        }
    }, hasSameNumber);
    return errorIds;
};

export const setSortForCBLTable = (
    data: Array<{ desc: boolean; id: string; }>,
    sortFunction: (sort: ISortModel) => void,
) => {
    if (data && data.length > 0) {
        const order = data[0].desc
            ? SORT_ORDER.DESCENDING
            : SORT_ORDER.ASCENDING;
        const sort = {
            column: data[0].id,
            order,
            desc: data[0].desc,
        };
        sortFunction(sort);
    }
};